import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faSpinner, faBuilding, faLayerGroup, faDoorOpen, faArrowRight, faArrowLeft, faTimes } from '@fortawesome/free-solid-svg-icons';
import apiServices from '../../../../services/apiService';
import { primaryColor, secondaryColor } from '../../../../utils/comon';
import PageHeadingSection from '../../../common/pageheading/PageHeadingSection';
import Breadcrumbs from '../../../common/BreadCrum/BreadCrum';

const defaultState = {
    siteName: "",
    countryName: "India",
    stateName: "Maharashtra",
    cityName: "Mumbai",
    pincode: "",
    isActive: 1,
    wings: []
};

const SiteSetupWizard = () => {
    const navigate = useNavigate();
    const [currentStep, setCurrentStep] = useState(1);
    const [isLoading, setIsLoading] = useState(false);
    const [payload, setPayload] = useState(() => {
        const saved = localStorage.getItem('siteBuilderDraft');
        if (saved) {
            try {
                return JSON.parse(saved);
            } catch (e) {
                return defaultState;
            }
        }
        return defaultState;
    });

    useEffect(() => {
        localStorage.setItem('siteBuilderDraft', JSON.stringify(payload));
    }, [payload]);

    const handleSiteChange = (e) => {
        const { name, value } = e.target;
        setPayload(prev => ({ ...prev, [name]: value }));
    };

    const addWing = () => {
        setPayload(prev => ({
            ...prev,
            wings: [...prev.wings, { wingName: "", isActiveId: 1, floors: [] }]
        }));
    };

    const updateWing = (wingIndex, value) => {
        setPayload(prev => {
            const newWings = [...prev.wings];
            newWings[wingIndex].wingName = value;
            return { ...prev, wings: newWings };
        });
    };

    const addFloor = (wingIndex) => {
        setPayload(prev => {
            const newWings = [...prev.wings];
            newWings[wingIndex].floors.push({ floorName: "", floorType: "Normal", isActiveId: 1, units: [] });
            return { ...prev, wings: newWings };
        });
    };

    const updateFloor = (wingIndex, floorIndex, field, value) => {
        setPayload(prev => {
            const newWings = [...prev.wings];
            newWings[wingIndex].floors[floorIndex][field] = value;
            return { ...prev, wings: newWings };
        });
    };

    const addUnit = (wingIndex, floorIndex) => {
        setPayload(prev => {
            const newWings = [...prev.wings];
            newWings[wingIndex].floors[floorIndex].units.push({ unitNumber: "", unitName: "", isActive: 1 });
            return { ...prev, wings: newWings };
        });
    };

    const updateUnit = (wingIndex, floorIndex, unitIndex, field, value) => {
        setPayload(prev => {
            const newWings = [...prev.wings];
            newWings[wingIndex].floors[floorIndex].units[unitIndex][field] = value;
            return { ...prev, wings: newWings };
        });
    };

    const validateStep1 = () => {
        if (!payload.siteName) return toast.warning("Site Name is required"), false;
        if (!payload.pincode) return toast.warning("Pincode is required"), false;
        return true;
    };

    const validateStep2 = () => {
        if (payload.wings.length === 0) return toast.warning("At least one wing is required"), false;
        for (let w of payload.wings) {
            if (!w.wingName) return toast.warning("All wings must have a name"), false;
            if (w.floors.length === 0) return toast.warning(`Wing ${w.wingName} must have at least one floor`), false;
            for (let f of w.floors) {
                if (!f.floorName) return toast.warning("All floors must have a name"), false;
                if (f.units.length === 0) return toast.warning(`Floor ${f.floorName} in Wing ${w.wingName} must have at least one unit`), false;
                for (let u of f.units) {
                    if (!u.unitNumber) return toast.warning("All units must have a number"), false;
                }
            }
        }
        return true;
    };

    const handleNext = () => {
        if (currentStep === 1 && !validateStep1()) return;
        if (currentStep === 2 && !validateStep2()) return;
        setCurrentStep(prev => prev + 1);
    };

    const handleBack = () => {
        setCurrentStep(prev => prev - 1);
    };

    const handleSubmit = async () => {
        if (!validateStep1() || !validateStep2()) return;
        setIsLoading(true);

        try {
            const res = await apiServices({
                method: 'POST',
                url: '/Residential/ResSiteDetailsMaster/UnifiedBulkCreate',
                data: payload
            });

            if (res.success || res.data?.success) {
                toast.success("Site structure created successfully!");
                localStorage.removeItem('siteBuilderDraft');
                navigate('/zentres/site-management/site');
            } else {
                toast.error(res.message || res.data?.message || "Failed to create site structure.");
            }
        } catch (error) {
            console.error(error);
            toast.error("An error occurred during submission.");
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="bg-gray-50 px-6 py-4 flex flex-col rounded-lg flex-1 overflow-y-auto">
            <Breadcrumbs />
            <PageHeadingSection
                Navbar={true}
                mainHeading="Unified Site Builder"
                subHeading="Create your complete site hierarchy in one go."
            />

            <div className="mt-6 max-w-5xl mx-auto w-full bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="flex border-b border-gray-100">
                    <div className={`flex-1 py-4 text-center font-bold text-sm ${currentStep >= 1 ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-400'}`}>
                        <FontAwesomeIcon icon={faBuilding} className="mr-2" /> 1. Site Details
                    </div>
                    <div className={`flex-1 py-4 text-center font-bold text-sm ${currentStep >= 2 ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-400'}`}>
                        <FontAwesomeIcon icon={faLayerGroup} className="mr-2" /> 2. Wings, Floors & Units
                    </div>
                    <div className={`flex-1 py-4 text-center font-bold text-sm ${currentStep === 3 ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-400'}`}>
                        <FontAwesomeIcon icon={faCheckCircle} className="mr-2" /> 3. Review & Submit
                    </div>
                </div>

                <div className="p-8 min-h-[400px]">
                    {currentStep === 1 && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 animate-in fade-in slide-in-from-right-8 duration-300">
                            <div>
                                <label className="block text-xs font-bold text-gray-700 dark:text-textsecondary mb-1">Site Name <span className="text-red-500">*</span></label>
                                <input type="text" name="siteName" value={payload.siteName} onChange={handleSiteChange} className="w-full px-4 py-2.5 bg-gray-50 dark:bg-actionitembg border border-gray-200 dark:border-bordercolor rounded-xl focus:bg-white dark:focus:bg-transparent focus:border-blue-500 dark:focus:border-gray-500 text-gray-800 dark:text-textprimary outline-none transition-all" placeholder="e.g. Phoenix Market City" />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-gray-700 dark:text-textsecondary mb-1">Pincode <span className="text-red-500">*</span></label>
                                <input type="number" name="pincode" value={payload.pincode} onChange={handleSiteChange} className="w-full px-4 py-2.5 bg-gray-50 dark:bg-actionitembg border border-gray-200 dark:border-bordercolor rounded-xl focus:bg-white dark:focus:bg-transparent focus:border-blue-500 dark:focus:border-gray-500 text-gray-800 dark:text-textprimary outline-none transition-all" placeholder="e.g. 400604" />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-gray-700 dark:text-textsecondary mb-1">Country</label>
                                <input type="text" name="countryName" value={payload.countryName} onChange={handleSiteChange} className="w-full px-4 py-2.5 bg-gray-50 dark:bg-actionitembg border border-gray-200 dark:border-bordercolor rounded-xl outline-none text-gray-800 dark:text-textprimary" disabled />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-gray-700 dark:text-textsecondary mb-1">State</label>
                                <input type="text" name="stateName" value={payload.stateName} onChange={handleSiteChange} className="w-full px-4 py-2.5 bg-gray-50 dark:bg-actionitembg border border-gray-200 dark:border-bordercolor rounded-xl outline-none text-gray-800 dark:text-textprimary" disabled />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-gray-700 dark:text-textsecondary mb-1">City</label>
                                <input type="text" name="cityName" value={payload.cityName} onChange={handleSiteChange} className="w-full px-4 py-2.5 bg-gray-50 dark:bg-actionitembg border border-gray-200 dark:border-bordercolor rounded-xl outline-none text-gray-800 dark:text-textprimary" disabled />
                            </div>
                        </div>
                    )}

                    {currentStep === 2 && (
                        <div className="space-y-6 animate-in fade-in slide-in-from-right-8 duration-300">
                            {payload.wings.map((wing, wIdx) => (
                                <div key={wIdx} className="bg-gray-50 p-6 rounded-2xl border border-gray-200 shadow-sm relative">
                                    <button onClick={() => {
                                        const newWings = payload.wings.filter((_, i) => i !== wIdx);
                                        setPayload(prev => ({ ...prev, wings: newWings }));
                                    }} className="absolute top-4 right-4 text-red-400 hover:text-red-600 text-sm font-bold">Remove Wing</button>
                                    
                                    <h4 className="text-lg font-black text-gray-800 mb-4 flex items-center gap-2">
                                        <FontAwesomeIcon icon={faBuilding} className="text-blue-500" /> Wing {wIdx + 1}
                                    </h4>
                                    
                                    <div className="mb-6">
                                        <label className="block text-xs font-bold text-gray-700 mb-1">Wing Name <span className="text-red-500">*</span></label>
                                        <input type="text" value={wing.wingName} onChange={(e) => updateWing(wIdx, e.target.value)} className="w-full md:w-1/2 px-4 py-2.5 bg-white border border-gray-200 rounded-xl focus:border-blue-500 outline-none transition-all" placeholder="e.g. Wing A" />
                                    </div>

                                    <div className="pl-4 md:pl-8 border-l-2 border-blue-100 space-y-6">
                                        {wing.floors.map((floor, fIdx) => (
                                            <div key={fIdx} className="bg-white p-5 rounded-xl border border-gray-100 shadow-sm relative">
                                                <button onClick={() => {
                                                    const newWings = [...payload.wings];
                                                    newWings[wIdx].floors = newWings[wIdx].floors.filter((_, i) => i !== fIdx);
                                                    setPayload(prev => ({ ...prev, wings: newWings }));
                                                }} className="absolute top-3 right-3 text-red-400 hover:text-red-600 text-xs font-bold">Remove Floor</button>
                                                
                                                <h5 className="font-bold text-gray-700 mb-3 flex items-center gap-2">
                                                    <FontAwesomeIcon icon={faLayerGroup} className="text-indigo-400" /> Floor {fIdx + 1}
                                                </h5>
                                                
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                                    <div>
                                                        <label className="block text-[10px] uppercase font-bold text-gray-500 mb-1">Floor Name</label>
                                                        <input type="text" value={floor.floorName} onChange={(e) => updateFloor(wIdx, fIdx, 'floorName', e.target.value)} className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg outline-none focus:border-indigo-400" placeholder="e.g. 1st Floor" />
                                                    </div>
                                                    <div>
                                                        <label className="block text-[10px] uppercase font-bold text-gray-500 mb-1">Type</label>
                                                        <select value={floor.floorType} onChange={(e) => updateFloor(wIdx, fIdx, 'floorType', e.target.value)} className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg outline-none">
                                                            <option value="Normal">Normal</option>
                                                            <option value="Parking">Parking</option>
                                                        </select>
                                                    </div>
                                                </div>

                                                <div className="pl-4 md:pl-8 space-y-3 mt-4">
                                                    {floor.units.map((unit, uIdx) => (
                                                        <div key={uIdx} className="flex flex-col md:flex-row gap-3 items-center bg-gray-50 p-3 rounded-lg border border-gray-100">
                                                            <FontAwesomeIcon icon={faDoorOpen} className="text-gray-400" />
                                                            <input type="text" value={unit.unitNumber} onChange={(e) => updateUnit(wIdx, fIdx, uIdx, 'unitNumber', e.target.value)} className="flex-1 px-3 py-1.5 bg-white border border-gray-200 rounded outline-none text-sm" placeholder="Unit No. (e.g. 101)" />
                                                            <input type="text" value={unit.unitName} onChange={(e) => updateUnit(wIdx, fIdx, uIdx, 'unitName', e.target.value)} className="flex-1 px-3 py-1.5 bg-white border border-gray-200 rounded outline-none text-sm" placeholder="Unit Name (Optional)" />
                                                            <button onClick={() => {
                                                                const newWings = [...payload.wings];
                                                                newWings[wIdx].floors[fIdx].units = newWings[wIdx].floors[fIdx].units.filter((_, i) => i !== uIdx);
                                                                setPayload(prev => ({ ...prev, wings: newWings }));
                                                            }} className="text-red-400 hover:text-red-600 px-2 py-1"><FontAwesomeIcon icon={faTimes}/></button>
                                                        </div>
                                                    ))}
                                                    <button onClick={() => addUnit(wIdx, fIdx)} className="text-xs font-bold text-indigo-600 hover:text-indigo-800 py-2">+ Add Unit</button>
                                                </div>
                                            </div>
                                        ))}
                                        <button onClick={() => addFloor(wIdx)} className="text-sm font-bold text-blue-600 hover:text-blue-800 bg-white px-4 py-2 rounded-lg border border-blue-100 shadow-sm">+ Add Floor</button>
                                    </div>
                                </div>
                            ))}
                            
                            <button onClick={addWing} className="w-full py-4 border-2 border-dashed border-gray-300 rounded-2xl text-gray-500 font-bold hover:bg-gray-100 hover:border-gray-400 transition-colors">
                                <FontAwesomeIcon icon={faBuilding} className="mr-2"/> Add New Wing
                            </button>
                        </div>
                    )}

                    {currentStep === 3 && (
                        <div className="text-center py-10 animate-in fade-in slide-in-from-right-8 duration-300">
                            <div className="w-20 h-20 bg-green-100 text-green-500 rounded-full flex items-center justify-center mx-auto mb-6">
                                <FontAwesomeIcon icon={faCheckCircle} className="text-4xl" />
                            </div>
                            <h3 className="text-2xl font-black text-gray-800 mb-2">Ready to Build!</h3>
                            <p className="text-gray-500 max-w-md mx-auto mb-8">You are about to create <strong>{payload.siteName}</strong> containing <strong>{payload.wings.length} wings</strong>. This will instantly configure your entire site structure.</p>
                            
                            <div className="bg-gray-50 p-6 rounded-2xl text-left text-xs text-gray-500 font-mono overflow-auto max-w-xl mx-auto shadow-inner border border-gray-200 max-h-64">
                                {JSON.stringify(payload, null, 2)}
                            </div>
                        </div>
                    )}
                </div>

                <div className="p-6 border-t border-gray-100 bg-gray-50 flex justify-between">
                    <button 
                        onClick={currentStep === 1 ? () => navigate(-1) : handleBack}
                        className="px-6 py-2.5 rounded-xl font-bold text-gray-600 bg-white border border-gray-200 hover:bg-gray-100 transition-all shadow-sm"
                    >
                        <FontAwesomeIcon icon={faArrowLeft} className="mr-2" /> {currentStep === 1 ? 'Cancel' : 'Back'}
                    </button>
                    
                    {currentStep < 3 ? (
                        <button 
                            onClick={handleNext}
                            className="px-6 py-2.5 rounded-xl font-bold text-white shadow-md hover:shadow-lg transition-all"
                            style={{ backgroundColor: primaryColor() }}
                        >
                            Next Step <FontAwesomeIcon icon={faArrowRight} className="ml-2" />
                        </button>
                    ) : (
                        <button 
                            onClick={handleSubmit}
                            disabled={isLoading}
                            className="px-8 py-2.5 rounded-xl font-bold text-white shadow-lg hover:shadow-xl transition-all"
                            style={{ backgroundColor: secondaryColor() }}
                        >
                            {isLoading ? <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" /> : <FontAwesomeIcon icon={faCheckCircle} className="mr-2" />}
                            Create Site Structure
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default SiteSetupWizard;
